import React from 'react';

import { aiConfigItemMessages, aiMessages } from '@atlaskit/editor-common/ai-messages';
import {
	AI_ASK_ROVO_MENU_ITEM,
	AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD,
	AI_IMPROVE_WRITING_MENU_ITEM,
	AI_POLISH_MENU_ITEM,
} from '@atlaskit/editor-common/block-menu';
import { AICommandIcon } from '@atlaskit/editor-toolbar';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import { RovoIcon } from '@atlaskit/logo';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { EditorPluginAIProvider } from '../../types/types';
import { improveWriting } from '../prebuilt/config-items/prompts/enhance/enhance';
import { addPolish } from '../prebuilt/config-items/prompts/polish/polish';

import type { BlockMenuActionDef } from './types';

const SMALL_WORD_COUNT_THRESHOLD = 5;

export const getBlockMenuActions = ({
	editorPluginAIProvider,
}: {
	editorPluginAIProvider: EditorPluginAIProvider;
}): BlockMenuActionDef[] => {
	const actions: BlockMenuActionDef[] = [
		{
			key: AI_ASK_ROVO_MENU_ITEM.key,
			label: aiMessages.askRovoToolbarIconTitle,
			elemBefore: <RovoIcon label="" size="xxsmall" />,
			configItem: editorPluginAIProvider.baseGenerate,
			shouldShowInPrimary: () =>
				true && expValEquals('platform_editor_ai_blockmenu_integration', 'isEnabled', true),
		},
		{
			key: AI_IMPROVE_WRITING_MENU_ITEM.key,
			label: aiConfigItemMessages.improveWritingTitle,
			elemBefore: <AICommandIcon label="" size="small" />,
			configItem: improveWriting(),
			shouldShowInPrimary: ({ textLength, wordCount }) =>
				wordCount >= SMALL_WORD_COUNT_THRESHOLD &&
				textLength < AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD &&
				expValEquals('platform_editor_ai_blockmenu_integration', 'isEnabled', true),
		},
	];

	// When remix experiment is enabled, the Remix action is registered by
	// editor-plugin-malleable-ui (which owns the openRemixModal command).
	// We only skip the polish/improve-formatting action here.
	if (!expValEquals('platform_editor_remix_in_block_menu', 'isEnabled', true)) {
		actions.push({
			key: AI_POLISH_MENU_ITEM.key,
			label: expValEquals('platform_editor_ai_rename_add_polish', 'isEnabled', true)
				? aiConfigItemMessages.improveFormattingTitle
				: aiConfigItemMessages.addPolishTitle,
			elemBefore: <AiGenerativeCleanupIcon label="" size="small" />,
			configItem: addPolish(),
			shouldShowInPrimary: ({ textLength }) =>
				textLength >= AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD &&
				expValEquals('platform_editor_ai_blockmenu_integration', 'isEnabled', true),
		});
	}

	return actions;
};
